import React, { ChangeEvent, FormEvent, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Checkbox, Icon, Dimmer, Loader } from 'semantic-ui-react';
import "./style.less";
import DatePicker from 'react-datepicker';
import { Service, ServiceAddOn } from '../../types/Service';
import { formatDateWithTZ } from '../../../utils/timezone';
import momentTZ from 'moment-timezone';
import sendToast from '../../../utils/Toast';
import UserContext from '../../../contexts/UserContext';
import { FetchOrderFromOrderIdParams, RequestInstance } from '../../types/OrderStatus';
import { cancelOrder, createOrder, fetchOrderImageUploadUrl, fetchOrdersFromOrderId, getOrderImage, updateOrder } from '../../services/OrderStatus';
import { uploadToSignedUrl } from '../../../services/imageUpload';
import { fetchServiceImage } from '../../services/Service';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import { getServiceInstanceImage } from '../../services/ImageUploader';
import { formatOrdersForTableView, getOrderNumber } from '../../../utils';
import { settleAll } from '../../utils/async';
import CloseAssociatedOrdersModal from './CloseAssociatedOrdersModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import OrderCommentsSection from './OrderCommentsSection';
import ServiceCard from '../ServiceOrderWidget/ServiceCard';
import { getAssetById } from '../../services/Asset';
import { useFacility } from '../../contexts/facility/FacilityContext';
import PaymentStatusBadge from '../PaymentStatusBadge';
import { getOrderDisplayTotal, getOrderPaymentReasonLabel, getPaymentStateForOrder, hasChargedPaymentState, isPaymentRequiredForService } from '../../utils/paymentStatus';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import { getResidentDisplayFullName } from '../../utils/residentIdentity';

interface Props {
    request?: RequestInstance;
    service: Service;
    selectedCategory: string;
    setOpenCreateOrderModal: React.Dispatch<React.SetStateAction<boolean>>;
    refreshData?: () => void;
}

type OrderImageJob = { name: string; kind: 'main' | 'additional' };
type SignedOrderImage = OrderImageJob & { url: string };

const MAX_CONCURRENT_ORDER_IMAGE_FETCHES = 6;

const collectOrderImageJobs = (request: RequestInstance): OrderImageJob[] => {
    const jobs: OrderImageJob[] = [];
    const seen: { [name: string]: boolean } = {};
    if (request.ImageName) {
        seen[request.ImageName] = true;
        jobs.push({ name: request.ImageName, kind: 'main' });
    }
    if (!Array.isArray(request.AdditionalImages)) return jobs;
    request.AdditionalImages.forEach((image) => {
        const name = image && image.name;
        if (!name || seen[name]) return;
        seen[name] = true;
        jobs.push({ name, kind: 'additional' });
    });
    return jobs;
};

const signOrderImage = async (request: RequestInstance, job: OrderImageJob): Promise<SignedOrderImage> => {
    const imgResult = await getOrderImage(request.Facility, String(request._id), job.name);
    if (imgResult && imgResult.Result && imgResult.Result.Content && imgResult.Result.Success) {
        return { name: job.name, kind: job.kind, url: String(imgResult.Result.Content) };
    }
    throw new Error((imgResult && imgResult.Result && imgResult.Result.Error) || 'Failed to get order image');
};

const signOrderImages = async (
    request: RequestInstance,
    jobs: OrderImageJob[],
    isCancelled: () => boolean,
): Promise<{ signed: SignedOrderImage[]; failed: number }> => {
    const signed: SignedOrderImage[] = [];
    let failed = 0;
    let nextIndex = 0;
    const runNext = async (): Promise<void> => {
        if (isCancelled()) return;
        const currentIndex = nextIndex++;
        if (currentIndex >= jobs.length) return;
        const [result] = await settleAll([signOrderImage(request, jobs[currentIndex])]);
        if (!isCancelled()) {
            if (result.status === 'fulfilled') signed.push(result.value);
            else failed += 1;
        }
        return runNext();
    };
    if (jobs.length > 0) {
        const workerCount = Math.min(MAX_CONCURRENT_ORDER_IMAGE_FETCHES, jobs.length);
        await Promise.all(Array.from({ length: workerCount }, () => runNext()));
    }
    return { signed, failed };
}

const CreateServiceOrder = ({ request, service, selectedCategory, setOpenCreateOrderModal, refreshData }: Props) => {

    const [selectedServiceAddOns, setSelectedServiceAddOns] = useState<ServiceAddOn[]>([]);
    const [orderName, setOrderName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imageLink, setImageLink] = useState<string>('');
    const [additionalImageLinks, setAdditionalImageLinks] = useState<{ name: string; url: string }[]>([]);
    const [currentImage, setCurrentImage] = useState<string>('');
    const [associatedOrders, setAssociatedOrders] = useState<RequestInstance[]>([]);
    const [openCloseAssociatedOrdersModal, setOpenCloseAssociatedOrdersModal] = useState<boolean>(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
    const [asset, setAsset] = useState<string>('');
    const [lightboxSrc, setLightboxSrc] = useState<string>('');
    const [photosFailed, setPhotosFailed] = useState<boolean>(false);
    const [photoLoadAttempt, setPhotoLoadAttempt] = useState<number>(0);
    const [photosLoading, setPhotosLoading] = useState(false);
    const { facilityTimezone, loading: facilityLoading } = useFacility();

    const history = useHistory();
    const serviceAddOns = (service && service.ServiceAddOns) ? service.ServiceAddOns : [];

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const activeResident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents because neighbors web only supports the resident who is logged in and we can't deselect or change it
    const { resolvedFacilityPaymentsEnabled, loading: paymentAccessLoading } = useNeighborsPaymentAccess();

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                setLoading(true);
                const assets = await getAssetById({
                    Facility: activeResident.Facility,
                    AssetId: service.Asset,
                });
                setAsset(assets?.response?.AssetName || '');
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'Failed to fetch assets');
            } finally {
                setLoading(false);
            }
        };
        fetchAssets();
    }, [request, activeResident.Facility, service.Asset])

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const allAddOns = service && service.ServiceAddOns ? service.ServiceAddOns : [];
                if (request) { // Edit view
                    setOrderName(request.Name || '');
                    setDescription(request.Description || '');
                    setStartDate(request.ScheduledTime?.startDate || '');
                    if (request.ServiceAddOns && Array.isArray(request.ServiceAddOns) && request.ServiceAddOns.length > 0)
                        setSelectedServiceAddOns(request.ServiceAddOns);
                    else {
                        const requiredAddons: ServiceAddOn[] = [];
                        allAddOns.map((addon: ServiceAddOn) => {
                            if (addon.required)
                                requiredAddons.push(addon);
                        });
                        setSelectedServiceAddOns(requiredAddons);
                    }
                    setImageLink(request.ImageUrl || '');
                    setAdditionalImageLinks([]);
                    setLightboxSrc('');
                    setPhotosFailed(false);
                    if (request.ImageName) {
                        setCurrentImage(request.ImageName);
                    }
                    const jobs = collectOrderImageJobs(request);
                    setPhotosLoading(jobs.length > 0);
                    const isCancelled = () => cancelled;
                    const serviceImagePromise = service.image && !service.imageURL
                        ? getServiceImage(isCancelled)
                        : Promise.resolve();
                    const [photoResult] = await Promise.all([
                        signOrderImages(request, jobs, isCancelled),
                        serviceImagePromise,
                    ]);
                    if (!cancelled) {
                        const main = photoResult.signed.find((image) => image.kind === 'main');
                        setImageLink(main ? main.url : (jobs.some((job) => job.kind === 'main') ? '' : (request.ImageUrl || '')));
                        setAdditionalImageLinks(
                            photoResult.signed
                                .filter((image) => image.kind === 'additional')
                                .map(({ name, url }) => ({ name, url })),
                        );
                        const noneSigned = jobs.length > 0 && photoResult.signed.length === 0;
                        setPhotosFailed(noneSigned);
                        if (noneSigned) {
                            sendToast('error', "Couldn't load photos");
                        } else if (photoResult.failed > 0) {
                            sendToast('error', photoResult.failed === 1
                                ? 'Failed to load one order image'
                                : `Failed to load ${photoResult.failed} order images`);
                        }
                    }
                } else { // create view (we need required addons checked in both edit and create views though)
                    const requiredAddons: ServiceAddOn[] = [];
                    allAddOns.map((addon: ServiceAddOn) => {
                        if (addon.required)
                            requiredAddons.push(addon);
                    });
                    setSelectedServiceAddOns(requiredAddons);
                }
            } finally {
                if (!cancelled) {
                    setPhotosLoading(false);
                    setLoading(false);
                }
            }
        }
        )();
        return () => { cancelled = true; };
    }, [request, service, photoLoadAttempt]);

    useEffect(() => {
        // Wait for facility timezone so associated-order times aren't formatted
        // against an empty zone and then left stale.
        if (facilityLoading) return;
        // `request` is display-formatted upstream by formatOrdersForTableView, which
        // turns a missing OrderId into the literal string '-' — treat that sentinel
        // the same as "no grouping id" instead of fetching associations for it.
        if (request && request.OrderId && request.OrderId !== '-') {
            fetchAllAssociatedOrders();
        }
    }, [request, facilityTimezone, facilityLoading]);

    const fetchAllAssociatedOrders = async () => {
        try {
            const orderFilter: Partial<FetchOrderFromOrderIdParams> = {
                facilityId: activeResident.Facility,
                statusArr: ["Open", "Accepted"],
                residentId: String(activeResident._id),
                OrderId: request && request.OrderId
            };
            const response = await fetchOrdersFromOrderId(orderFilter);
            const formattedOrders = formatOrdersForTableView(response, facilityTimezone);
            setAssociatedOrders(formattedOrders);
        } catch (error) {
            sendToast("error", error instanceof Error ? error.message : 'Failed to fetch associated orders');
        }
    }

    const getServiceImage = async (isCancelled: () => boolean) => {
        if (service.image) {
            try {
                let imgRes = null;
                if (request && request.OriginalServiceInstanceId) {
                    imgRes = await getServiceInstanceImage({ svcInstanceId: String(service._id), facilityId: service.Facility, fileName: service.image });
                } else {
                    imgRes = await fetchServiceImage({ serviceId: String(service._id), Facility: service.Facility, fileName: service.image });
                }
                if (isCancelled()) return;
                service.imageURL = imgRes || "";
            } catch (error) {
                if (isCancelled()) return;
                sendToast('error', error instanceof Error ? error.message : 'Failed to get service image');
            }
        }
    };

    const handleServiceAddons = (id: string, value: boolean) => {
        let updatedAddOns = JSON.parse(JSON.stringify(selectedServiceAddOns)); // deep copy of the selectedServiceAddOns state array
        if (value) {
            serviceAddOns.forEach(((addon) => {
                if (addon.id === id) {
                    updatedAddOns.push(addon);
                }
            }));
        } else {
            updatedAddOns = updatedAddOns.filter((addon: ServiceAddOn) => addon.id !== id);
        }
        setSelectedServiceAddOns(updatedAddOns);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!service || !activeResident || !activeResident.Unit || !startDate || !orderName) {
            sendToast('warn', 'Missing required fields to create and order');
            return;
        }
        if (!moment(startDate).isValid()) {
            sendToast('warn', 'Invalid start date');
            return;
        }
        try {
            setLoading(true);
            const startDateMomentObj = momentTZ(startDate);
            const endDate = startDateMomentObj.clone().add(15, 'minutes'); // set end date to 15 minutes after the start date by default
            const order: Partial<RequestInstance> = {
                Category: service.category,
                ResidentId: String(activeResident._id),
                OrderName: orderName,
                Description: description,
                ScheduledTime: {
                    startDate: momentTZ(startDate).format('YYYY-MM-DDTHH:mm:ss'),
                    endDate: momentTZ(endDate).format('YYYY-MM-DDTHH:mm:ss'),
                },
                Facility: String(activeResident.Facility),
                RequestChannel: "Web",
                ...(selectedServiceAddOns && selectedServiceAddOns.length > 0 && { ServiceAddOns: selectedServiceAddOns }),
                OriginalServiceId: String(service._id)
            };
            const response = await createOrder(activeResident.Facility, order);
            if (!response.Result.Content) {
                throw new Error(response.Result.Error || 'Failed to create order');
            }
            const createdOrderId = response.Result.Content;
            // The order itself is already created at this point. Treat the image
            // attach as best-effort and always close/navigate away on success of
            // the order create, rather than leaving the form open for a retry that
            // would create a second, duplicate order.
            let imageWarning = '';
            if (selectedImage) {
                try {
                    await uploadImageFile(createdOrderId);
                    const updateRes = await updateOrder(
                        activeResident.Facility,
                        {
                            _id: createdOrderId,
                            ImageName: selectedImage.name,
                        });
                    if (!updateRes.Result.Content.updated && updateRes.Result.Content.message) { // backend will return updated as false with a message as to why updated is false
                        imageWarning = updateRes.Result.Content.message;
                    }
                } catch (imageError) {
                    imageWarning = imageError instanceof Error ? imageError.message : 'Failed to attach the image to the order';
                }
            }
            if (imageWarning) {
                sendToast('warn', `Order created, but the image could not be attached: ${imageWarning}`);
            } else {
                sendToast('success', 'Order created successfully');
            }
            setOpenCreateOrderModal(false);
            history.push('/order-status');
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to create order');
        } finally {
            setLoading(false);
        }
    };

    const uploadImageFile = async (orderId: string) => {
        if (selectedImage) {
            const putUrl = await fetchOrderImageUploadUrl(activeResident.Facility, orderId, selectedImage.name);
            const uploadRes = await uploadToSignedUrl(selectedImage, putUrl.Result.Content);
            if (!uploadRes.ok) {
                throw new Error('Failed to upload image');
            }
        }
    };

    const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const selectedFile = event.target.files[0];
            if (selectedFile) {
                setSelectedImage(selectedFile);
                setCurrentImage('');
                setImageLink('');
            }
        }
    };

    // Object URLs must be revoked once nothing references them, or each image
    // pick leaks a blob for the life of the tab.
    const selectedImagePreviewUrl = useMemo(
        () => (selectedImage ? URL.createObjectURL(selectedImage) : ''),
        [selectedImage],
    );
    useEffect(() => {
        if (!selectedImagePreviewUrl) return;
        return () => URL.revokeObjectURL(selectedImagePreviewUrl);
    }, [selectedImagePreviewUrl]);

    const cancelRequest = async () => {
        if (!request) {
            sendToast('warn', 'No request to cancel');
            return;
        }
        try {
            setLoading(true);
            await cancelOrder(request.Facility, String(request._id), request.ResidentId);
            sendToast('success', 'Order canceled successfully');
            setShowCancelConfirm(false);
            setOpenCreateOrderModal(false);
            setOpenCloseAssociatedOrdersModal(false);
            if (refreshData) refreshData();
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to cancel order');
        } finally {
            setLoading(false);
        }
    };
    
    const cancelAllAssociatedRequest = () => {
        setOpenCloseAssociatedOrdersModal(true);
    };

    const onClose = () => {
        setOpenCloseAssociatedOrdersModal(false);
    };

    const cancelAllOpenAndAcceptedOrders = async () => {
        if (associatedOrders && associatedOrders.length > 0) {
            try {
                setLoading(true);
                const cancelPromises = associatedOrders.map((order) => cancelOrder(order.Facility, String(order._id), order.ResidentId));
                const res = await Promise.all(cancelPromises);
                if (!res) throw new Error('Failed to cancel order');
                sendToast('success', 'Order canceled successfully');
                setOpenCreateOrderModal(false);
                setOpenCloseAssociatedOrdersModal(false);
                if (refreshData) refreshData();
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'Failed to cancel order');
            } finally {
                setLoading(false);
            }
        } else {
            sendToast('warn', `No request's to cancel`);
        }
    }

    const addOnsAndCostTotal =
        selectedServiceAddOns.reduce((acc, addon) => acc + Number(addon.itemCost || 0), 0) +
        Number(service && service.defaults && service.defaults.externalCost || 0);
    const totalCostForOrder = request ? getOrderDisplayTotal(request, addOnsAndCostTotal) : addOnsAndCostTotal;

    const paymentState = request ? getPaymentStateForOrder(request) : undefined;
    const paymentAmountCents = paymentState && (
        typeof paymentState.amountDisplayedCents === 'number'
            ? paymentState.amountDisplayedCents
            : paymentState.amountCents
    );
    const paymentAmountLabel = typeof paymentAmountCents === 'number'
        ? `$${(paymentAmountCents / 100).toFixed(2)}`
        : 'N/A';
    const paymentCurrency = String(paymentState?.currency || 'usd').toUpperCase();
    const paymentReasonLabel = getOrderPaymentReasonLabel(paymentState);
    // Facilities without card payments don't see this block unless the order has a payment record.
    // The service prop and the order's own service can disagree, so either one counts; the Payment Required row uses the same answer.
    const paymentRequired = resolvedFacilityPaymentsEnabled
        && (isPaymentRequiredForService(service) || isPaymentRequiredForService(request?.Service));
    const showPaymentInfo = hasChargedPaymentState(request) || (!paymentAccessLoading && paymentRequired);

    return (
        <div className="detail-modal-body">
            <Dimmer active={loading || facilityLoading} inverted>
                <Loader active={loading || facilityLoading} />
            </Dimmer>
            {request && (
                <div className="detail-header-row">
                    <div className={`order-status-badge ${(request.Status || '').toLowerCase()}`}>{request.Status}</div>
                    <div className="detail-order-id">Order #{getOrderNumber(String(request._id))}</div>
                    {(startDate || request.RequestedTime) && (
                        <div className="detail-header-date">
                            <Icon name="calendar outline" />
                            {startDate
                                ? momentTZ(startDate).format('MMM D, YYYY · h:mm A')
                                : formatDateWithTZ(request.RequestedTime, 'MMM D, YYYY · h:mm A', facilityTimezone || '')}
                        </div>
                    )}
                </div>
            )}
            <div className="detail-facts-grid">
                <div className="detail-fact">
                    <div className="detail-fact-label">Resident</div>
                    <div className="detail-fact-value">{getResidentDisplayFullName(activeResident)}</div>
                </div>
                <div className="detail-fact">
                    <div className="detail-fact-label">Room</div>
                    <div className="detail-fact-value">{activeResident.Unit && activeResident.Unit.Name ? activeResident.Unit.Name : 'N/A'}</div>
                </div>
                <div className="detail-fact">
                    <div className="detail-fact-label">Category</div>
                    <div className="detail-fact-value">{(request && request.Category) || selectedCategory || 'N/A'}</div>
                </div>
                <div className="detail-fact">
                    <div className="detail-fact-label">Service</div>
                    <div className="detail-fact-value">{service && service.name ? service.name : 'N/A'}</div>
                </div>
            </div>
            <div className="detail-divider" />
            <Form autoComplete="false" onSubmit={(e) => handleSubmit(e)}>
                {!request && (
                    <ServiceCard
                        service={service}
                        serviceImage={service.imageURL || ''}
                        asset={asset || ''}
                        source='createOrder'
                    />
                )}
                {(serviceAddOns.length > 0 || Number(service?.defaults?.externalCost) > 0) && (
                    <div className="addon-check-card">
                        {serviceAddOns.map((item: any) => {
                            const isAddonPresentInRequest = !!selectedServiceAddOns.find(
                                (addon) => addon.id == item.id,
                            ); // we check if the addon is present in the request object array if its there we mark it as checked else we mark is as unchecked
                            return (
                                <div
                                    key={item.id}
                                    className="addon-check-item-row"
                                >
                                    <div>
                                        <label style={{ flexGrow: 1, marginRight: '10px' }}>{item.itemName}</label>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {request ? (
                                                <div
                                                    className={`addon-checkbox${(item.required || isAddonPresentInRequest) ? ' checked' : ''}`}
                                                    role="checkbox"
                                                    aria-checked={item.required || isAddonPresentInRequest}
                                                    aria-disabled="true"
                                                    aria-label={item.itemName}
                                                >
                                                    {(item.required || isAddonPresentInRequest) && <Icon name="check" />}
                                                </div>
                                            ) : (
                                                <Checkbox
                                                    disabled={item.required}
                                                    checked={item.required || isAddonPresentInRequest}
                                                    onChange={(event: React.FormEvent<HTMLInputElement>, { checked }) =>
                                                        handleServiceAddons(item.id, checked as boolean)
                                                    }
                                                />
                                            )}
                                        </div>
                                        <div style={{ width: '50px', textAlign: 'right' }}>
                                            {request ? (
                                                <span>
                                                    <b>{Number(item.itemCost) || 0} $</b>
                                                </span>
                                            ) : (
                                                item.itemCost > 0 && (
                                                    <span>
                                                        <b>{item.itemCost} $</b>
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {Number(service?.defaults?.externalCost) > 0 && (
                            <div className="addon-check-item-row">
                                <div>
                                    <label style={{ flexGrow: 1, marginRight: '10px' }}>External Cost</label>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ width: '50px', textAlign: 'right' }}>
                                        <span>
                                            <b>{Number(service.defaults.externalCost)} $</b>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {totalCostForOrder > 0 && (
                            <div className="detail-total-row">
                                <span>
                                    <b>
                                        Total: ${totalCostForOrder.toFixed(2)}
                                    </b>
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {request && <div className="detail-section-label detail-request-details-label">Request Details</div>}
                {/* Order name */}
                <Form.Field className="detail-edit-field">
                    <label className="detail-edit-label">Order Name</label>
                    <Form.Input
                        required={true}
                        value={orderName || ''}
                        placeholder="Enter Order Name *"
                        onChange={(e) => setOrderName(e.target.value)}
                        readOnly={request ? true : false}
                    />
                </Form.Field>
                {/* Order description */}
                <Form.Field className="detail-edit-field">
                    <label className="detail-edit-label">Description</label>
                    <Form.TextArea
                        value={description || ''}
                        onChange={(e) => setDescription(e.currentTarget.value)}
                        placeholder="Order Notes"
                        readOnly={request ? true : false}
                    />
                </Form.Field>
                {request && request.StaffId && request.AcceptedByName && (
                    <p className="acceptedPara">This request has been assigned to {request.AcceptedByName}</p>
                )}
                {request && request.Status === 'Closed' ? (
                    request.ClosedByResident ? (
                        <p className="status">Canceled by resident</p>
                    ) : (
                        <p className="status">Closed by {request.ClosedByName}</p>
                    )
                ) : (
                    <></>
                )}
                {/* Scheduled time */}
                <Form.Field className="detail-edit-field">
                    <label className="detail-edit-label">{request ? 'Date' : 'Requested Time'}</label>
                    {request ? (
                        <input
                            className="detail-edit-input"
                            type="text"
                            readOnly
                            value={
                                startDate
                                    ? momentTZ(startDate).format('M.D.Y h:mm A')
                                    : formatDateWithTZ(request.RequestedTime, 'M.D.Y h:mm A', facilityTimezone || '')
                            }
                        />
                    ) : (
                        <DatePicker
                            customInput={
                                <input
                                    className="detail-edit-input"
                                    placeholder="Requested Time *"
                                    required={true}
                                    onChange={(e) => {}}
                                    value={startDate ? momentTZ(startDate).format('M.D.Y h:mm A') : ''}
                                />
                            }
                            minDate={new Date()} // Set the minimum to today
                            dateFormat="M.d.Y h:mm aa"
                            showTimeSelect
                            timeIntervals={15}
                            selected={startDate ? Date.parse(startDate || new Date().toISOString()) : null}
                            onChange={(date: Date) => {
                                if (!date) return;
                                const dateTime = momentTZ(date).format('YYYY-MM-DDTHH:mm:ss');
                                setStartDate(dateTime);
                            }}
                        />
                    )}
                </Form.Field>
                {/* Image input component */}
                {request ? (
                    <Form.Field className="detail-edit-field">
                        {imageLink || additionalImageLinks.length > 0 ? (
                            <div className="detail-images-sections">
                                {imageLink && (
                                    <div className="detail-images-section">
                                        <label className="detail-edit-label">Images</label>
                                        <div className="detail-images-grid">
                                            <div className="detail-image-thumb" onClick={() => setLightboxSrc(imageLink)}>
                                                <img src={imageLink} alt="Request" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {additionalImageLinks.length > 0 && (
                                    <div className="detail-images-section detail-images-section-extra">
                                        <label className="detail-edit-label">Additional Images</label>
                                        <div className="detail-images-grid">
                                            {additionalImageLinks.map((image) => (
                                                <div
                                                    key={image.name}
                                                    className="detail-image-thumb"
                                                    onClick={() => setLightboxSrc(image.url)}
                                                >
                                                    <img src={image.url} alt={image.name} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : photosLoading ? (
                            <>
                                <label className="detail-edit-label">Images</label>
                                <span className="detail-images-empty">Fetching photos</span>
                            </>
                        ) : photosFailed ? (
                            <>
                                <label className="detail-edit-label">Images</label>
                                <span className="detail-images-empty">Couldn't load photos</span>
                                <button
                                    type="button"
                                    className="detail-images-retry"
                                    onClick={() => {
                                        setPhotosLoading(true);
                                        setPhotoLoadAttempt((attempt) => attempt + 1);
                                    }}
                                >
                                    Retry
                                </button>
                            </>
                        ) : (
                            <>
                                <label className="detail-edit-label">Images</label>
                                <span className="detail-images-empty">No images uploaded</span>
                            </>
                        )}
                    </Form.Field>
                ) : (
                    <div className="detail-edit-field detail-image-picker" style={{ textAlign: 'center' }}>
                        <input
                            id="imageInput"
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleImageChange}
                        />
                        <div style={{ cursor: 'pointer' }}>
                            {!selectedImage && (
                                <label htmlFor="imageInput" style={{ cursor: 'pointer' }}>
                                    <Icon name="images" size="huge" />
                                </label>
                            )}
                            {selectedImagePreviewUrl && (
                                <label htmlFor="imageInput" style={{ cursor: 'pointer' }}>
                                    <img
                                        src={selectedImagePreviewUrl}
                                        style={{ maxWidth: '100px', maxHeight: '100px' }}
                                    />
                                </label>
                            )}
                        </div>
                    </div>
                )}
                {request && lightboxSrc && (
                    <div className="lightbox-overlay open" onClick={() => setLightboxSrc('')}>
                        <img src={lightboxSrc} alt="Request full size" />
                    </div>
                )}
                <div className="detail-save-row">
                    {!request && (
                        <Button
                            disabled={!orderName || (!startDate && !request)}
                            primary
                            fluid
                            className="detail-save-btn"
                            size="medium"
                            type="submit"
                        >
                            Send
                        </Button>
                    )}
                    {request && request.Status !== 'Closed' && (
                        <Button
                            fluid
                            size="medium"
                            className="detail-cancel-btn"
                            onClick={associatedOrders && associatedOrders.length > 1 ? cancelAllAssociatedRequest : () => setShowCancelConfirm(true)}
                            type="button"
                            disabled={loading}
                        >
                            Cancel Order
                        </Button>
                    )}
                </div>
                {request && showPaymentInfo && (
                    <>
                        <div className="detail-divider" />
                        <div className="detail-section-label">Payment Info</div>
                        <div className="detail-facts-grid">
                            <div className="detail-fact">
                                <div className="detail-fact-label">Payment Status</div>
                                <div className="detail-fact-value">
                                    <PaymentStatusBadge order={request} facilityPaymentsEnabled={resolvedFacilityPaymentsEnabled} />
                                </div>
                            </div>
                            <div className="detail-fact">
                                <div className="detail-fact-label">Payment Required</div>
                                <div className="detail-fact-value">{paymentRequired ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="detail-fact">
                                <div className="detail-fact-label">Amount</div>
                                <div className="detail-fact-value">{paymentAmountLabel}{paymentAmountLabel !== 'N/A' ? ` ${paymentCurrency}` : ''}</div>
                            </div>
                            {paymentState?.transactionId && (
                                <div className="detail-fact">
                                    <div className="detail-fact-label">Transaction</div>
                                    <div className="detail-fact-value">{paymentState.transactionId}</div>
                                </div>
                            )}
                            {paymentReasonLabel && (
                                <div className="detail-fact">
                                    <div className="detail-fact-label">Reason</div>
                                    <div className="detail-fact-value">{paymentReasonLabel}</div>
                                </div>
                            )}
                        </div>
                    </>
                )}
                {request && service?.defaults?.commentsEnabled && (
                    <>
                        <div className="detail-divider" />
                        <div className="detail-section-label">Comments</div>
                        <OrderCommentsSection
                            commentsEnabled={true}
                            facilityId={activeResident.Facility}
                            requestInstanceId={String(request._id)}
                            registrantId={String(activeResident._id)}
                            facilityTimezone={facilityTimezone || ''}
                            variant="modal"
                        />
                    </>
                )}
            </Form>
            <DeleteConfirmationModal
                open={showCancelConfirm}
                onClose={() => setShowCancelConfirm(false)}
                onConfirm={cancelRequest}
                loading={loading}
                headerText="Confirm Cancellation"
                headerIcon="cancel"
                confirmText="Cancel Order"
                confirmColor="red"
                dismissText="Keep Order"
                messageToDisplay="Are you sure you want to cancel this request?"
            />
            <CloseAssociatedOrdersModal
                open={openCloseAssociatedOrdersModal}
                onClose={onClose}
                cancelRequest={cancelRequest}
                cancelAllOpenAndAcceptedOrders={cancelAllOpenAndAcceptedOrders}
                associatedOrders={associatedOrders}
                facilityTimezone={facilityTimezone}
                loading={loading}
            />
        </div>
    );
};

export default CreateServiceOrder;